import React, { useEffect, useState } from 'react';
import getColorCode from 'src/utils/GetThemeColor';
import { Row, Col } from 'react-bootstrap';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import {
  createVehicleTrackingLineItemQualityCheckLog,
  getDimensionForQualityCheckCreateLog,
} from 'src/redux/Actions/VehicleTracking';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import { ReactComponent as TargetInfoIcon } from 'src/Images/icons/common/target-info-icon.svg';

export default function CreateQcLogDrawer({
  setDrawerStep,
  formData,
  setFormData,
  vehicleTrackingNumber,
  qualityCheckId,
}) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const tenantOrganisationId = authReducer.userData.organisations?.id;

  useEffect(() => {
    dispatch(
      getDimensionForQualityCheckCreateLog(
        tenantOrganisationId,
        qualityCheckId,
        vehicleTrackingNumber,
      ),
    );
  }, [dispatch, tenantOrganisationId, qualityCheckId, vehicleTrackingNumber]);

  const { getDimensionForQualityChecklog } = vehicleTrackingReducer;

  const handleSearchInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDimensionChange = (assignmentId, value) => {
    setFormData((prev) => {
      const exists = prev.dimensions.find(
        (d) => d.quality_check_dimension_assignment_id === assignmentId,
      );

      if (exists) {
        // update
        return {
          ...prev,
          dimensions: prev.dimensions.map((dim) =>
            dim.quality_check_dimension_assignment_id === assignmentId
              ? { ...dim, actual_value: value }
              : dim,
          ),
        };
      }
      // insert new
      return {
        ...prev,
        dimensions: [
          ...prev.dimensions,
          {
            quality_check_dimension_assignment_id: assignmentId,
            actual_value: value,
            actual_min_value: null,
            actual_max_value: null,
            actual_boolean: null,
            actual_option: null,
          },
        ],
      };
    });
  };
  const handleDropdownChange = (assignmentId, selectedOption) => {
    const selectedId = selectedOption ? selectedOption.value : null;

    setFormData((prev) => {
      const exists = prev.dimensions.find(
        (d) => d.quality_check_dimension_assignment_id === assignmentId,
      );

      if (exists) {
        // update
        return {
          ...prev,
          dimensions: prev.dimensions.map((dim) =>
            dim.quality_check_dimension_assignment_id === assignmentId
              ? { ...dim, actual_option: selectedId }
              : dim,
          ),
        };
      }
      // insert new
      return {
        ...prev,
        dimensions: [
          ...prev.dimensions,
          {
            quality_check_dimension_assignment_id: assignmentId,
            actual_value: null,
            actual_min_value: null,
            actual_max_value: null,
            actual_boolean: null,
            actual_option: selectedId,
          },
        ],
      };
    });
  };

  const handleBooleanChange = (assignmentId, value) => {
    setFormData((prev) => {
      const exists = prev.dimensions.find(
        (d) => d.quality_check_dimension_assignment_id === assignmentId,
      );

      if (exists) {
        // update
        return {
          ...prev,
          dimensions: prev.dimensions.map((dim) =>
            dim.quality_check_dimension_assignment_id === assignmentId
              ? { ...dim, actual_boolean: value }
              : dim,
          ),
        };
      }

      // insert new
      return {
        ...prev,
        dimensions: [
          ...prev.dimensions,
          {
            quality_check_dimension_assignment_id: assignmentId,
            actual_value: null,
            actual_min_value: null,
            actual_max_value: null,
            actual_boolean: value,
            actual_option: null,
          },
        ],
      };
    });
  };

  const handleCreateQualityCheck = () => {
    const createData = {
      tenant_organisation_id: tenantOrganisationId,
      quality_check_id: qualityCheckId,
      status: formData.statusKey,
      vehicle_tracking_number: formData.vehicle_tracking_number,
      vehicle_tracking_line_items: formData.vehicle_tracking_line_items.map(
        (item) => ({
          id: item.id,
          quantity: item.quantity ?? null,
        }),
      ),
      dimensions: formData.dimensions.map((d) => ({
        quality_check_dimension_assignment_id:
          d.quality_check_dimension_assignment_id,
        actual_value: d.actual_value ?? null,
        actual_min_value: d.actual_min_value ?? null,
        actual_max_value: d.actual_max_value ?? null,
        actual_boolean: d.actual_boolean ?? null,
        actual_option: d.actual_option ?? null,
      })),
    };

    dispatch(createVehicleTrackingLineItemQualityCheckLog(createData)).then(
      () => {
        window.location.reload();
      },
    );
  };

  const [focusedQualityCheckDimensionId, setFocusedQualityCheckDimensionId] =
    useState(null);

  const [hoverQualityCheckDimensionId, setHoverQualityCheckDimensionId] =
    useState(null);

  // Input field extra data onFocus and hover
  function getTheFieldExtraData(dimensionObj) {
    if (
      dimensionObj?.target_max_value !== null &&
      dimensionObj?.target_min_value !== null &&
      dimensionObj?.target_value !== null
    ) {
      return (
        <span className="text-sm text-grey div-flex-row div-align-center animate-qc-extra-data">
          Target Value: {dimensionObj?.target_value} | Min:
          {dimensionObj?.target_min_value} & Max:{' '}
          {dimensionObj?.target_max_value}
        </span>
      );
    }

    if (
      dimensionObj?.target_max_value !== null &&
      dimensionObj?.target_min_value !== null
    ) {
      return (
        <span className="text-sm text-grey div-flex-row div-align-center animate-qc-extra-data">
          Target Min Value:{dimensionObj?.target_min_value} Target Max Value:{' '}
          {dimensionObj?.target_max_value}
        </span>
      );
    }

    if (dimensionObj?.target_value !== null) {
      return (
        <span className="text-sm text-grey div-flex-row div-align-center animate-qc-extra-data">
          Target Value: {dimensionObj?.target_value}
        </span>
      );
    }

    if (
      dimensionObj?.target_boolean === true ||
      dimensionObj?.target_boolean === false
    ) {
      return (
        <span className="text-sm text-grey div-flex-row div-align-center animate-qc-extra-data">
          Target Boolean: {dimensionObj?.target_boolean ? 'True' : 'False'}
        </span>
      );
    }

    if (dimensionObj?.target_option?.length > 0) {
      return (
        <span className="text-sm text-grey div-flex-row div-align-center animate-qc-extra-data">
          Target Value: {dimensionObj?.target_option[0]?.name}
        </span>
      );
    }

    return '';
  }

  return (
    <div className="page-layout div-flex-column rg-10">
      <div className="div-flex-row horizontal-padding-16 border-bottom div-space-between div-align-center">
        <div className="w-100">
          <span className="text-xxl text-bold">Step 2: Quality Check</span>
        </div>
        <div className="filter-list-item div-flex-column rg-5 w-50">
          <div className="div-flex-row cg-5">
            <div className="input-wrapper">
              <select
                name="statusKey"
                className="form-input-section input-icon-remove"
                value={formData.statusKey}
                onChange={handleSearchInputChange}
              >
                <option value="3">Approved</option>
                <option value="4">Reject</option>
              </select>
              <span className="form-input-section-icon">
                <DropdownIcon
                  className="icon-size-sm"
                  color={getColorCode(themeColor)}
                />
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`quality-check-dawer-height horizontal-padding-16  padding-bottom-10  div-flex-column rg-30 theme-scrollable-container-${themeColor}`}
      >
        <div className="div-flex-column rg-30">
          {Array.isArray(getDimensionForQualityChecklog) &&
            getDimensionForQualityChecklog
              .filter(
                (dim) => dim.data_type?.id === 1 || dim.data_type?.id === 2,
              )
              .map((dimension) => (
                <div
                  key={dimension.quality_check_dimension_assignment_id}
                  className="div-flex-column rg-5 qc-icon-padding border-radius-4"
                >
                  <div className="div-flex-row div-space-between">
                    <span className="text-md div-flex-row div-align-center cg-5">
                      <span className="text-md text-semi-bold">
                        {dimension.dimension_name}
                      </span>
                      <span
                        onMouseEnter={() =>
                          setHoverQualityCheckDimensionId(
                            dimension.quality_check_dimension_assignment_id,
                          )
                        }
                        onMouseLeave={() =>
                          setHoverQualityCheckDimensionId(null)
                        }
                        className="mb-1 cursor-pointer"
                      >
                        <TargetInfoIcon className="icon-size-sm" />
                      </span>
                      {hoverQualityCheckDimensionId ===
                        dimension.quality_check_dimension_assignment_id &&
                        focusedQualityCheckDimensionId !==
                          dimension.quality_check_dimension_assignment_id &&
                        getTheFieldExtraData(dimension)}
                      {focusedQualityCheckDimensionId ===
                        dimension.quality_check_dimension_assignment_id &&
                        getTheFieldExtraData(dimension)}
                    </span>
                  </div>

                  <Row>
                    <Col xxl={12} xl={12} lg={12}>
                      <div className="qc-input-group w-100">
                        <input
                          className="qc-input-field text-sm"
                          placeholder={`Enter ${dimension.dimension_name}`}
                          type="number"
                          onWheel={(e) => e.target.blur()}
                          value={
                            formData.dimensions?.find(
                              (d) =>
                                d.quality_check_dimension_assignment_id ===
                                dimension.quality_check_dimension_assignment_id,
                            )?.actual_value || ''
                          }
                          onFocus={() =>
                            setFocusedQualityCheckDimensionId(
                              dimension.quality_check_dimension_assignment_id,
                            )
                          }
                          onBlur={() => setFocusedQualityCheckDimensionId(null)}
                          onChange={(e) => {
                            const val =
                              e.target.value === ''
                                ? ''
                                : Number(e.target.value);
                            handleDimensionChange(
                              dimension.quality_check_dimension_assignment_id,
                              val,
                            );
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
        </div>
        {/* Boolean */}
        <div className="div-flex-column rg-30">
          {Array.isArray(getDimensionForQualityChecklog) &&
            getDimensionForQualityChecklog
              .filter((dim) => dim.data_type?.id === 5)
              .map((dimension) => {
                const storedVal = formData.dimensions.find(
                  (d) =>
                    d.quality_check_dimension_assignment_id ===
                    dimension.quality_check_dimension_assignment_id,
                )?.actual_boolean;

                return (
                  <div
                    key={dimension.quality_check_dimension_assignment_id}
                    className="div-flex-column  rg-5 qc-icon-padding border-radius-4"
                  >
                    <div className="div-flex-row div-space-between">
                      <span className="text-md div-flex-row div-align-center cg-5">
                        <span className="text-md text-semi-bold">
                          {dimension.dimension_name}
                        </span>
                        <span
                          onMouseEnter={() =>
                            setHoverQualityCheckDimensionId(
                              dimension.quality_check_dimension_assignment_id,
                            )
                          }
                          onMouseLeave={() =>
                            setHoverQualityCheckDimensionId(null)
                          }
                          className="mb-1 cursor-pointer"
                        >
                          <TargetInfoIcon className="icon-size-sm" />
                        </span>
                        {hoverQualityCheckDimensionId ===
                          dimension.quality_check_dimension_assignment_id &&
                          focusedQualityCheckDimensionId !==
                            dimension.quality_check_dimension_assignment_id &&
                          getTheFieldExtraData(dimension)}
                        {focusedQualityCheckDimensionId ===
                          dimension.quality_check_dimension_assignment_id &&
                          getTheFieldExtraData(dimension)}
                      </span>
                    </div>

                    <div className="div-flex-column rg-10">
                      {/* TRUE toggle */}
                      <div className="qc-list-data div-flex-row div-space-between border border-radius-4">
                        <span className="text-sm div-align-center text-grey">
                          If the quality check condition is
                        </span>
                        <div className="div-flex-row div-align-center cg-10">
                          <span className="text-sm text-green bg-green qc-boolean-padding border-green border-radius-4">
                            True
                          </span>
                          <input
                            type="checkbox"
                            className="quality-check-checkbox"
                            checked={storedVal === true}
                            onChange={(e) =>
                              handleBooleanChange(
                                dimension.quality_check_dimension_assignment_id,
                                e.target.checked ? true : null,
                              )
                            }
                          />
                        </div>
                      </div>

                      {/* FALSE toggle */}
                      <div className="qc-list-data div-flex-row div-space-between border border-radius-4">
                        <span className="text-sm div-align-center text-grey">
                          If the quality check condition is
                        </span>
                        <div className="div-flex-row div-align-center cg-10">
                          <span className="text-sm text-red qc-bg-red qc-boolean-padding border-red border-radius-4">
                            False
                          </span>
                          <input
                            type="checkbox"
                            className="quality-check-checkbox"
                            checked={storedVal === false}
                            onChange={(e) =>
                              handleBooleanChange(
                                dimension.quality_check_dimension_assignment_id,
                                e.target.checked ? false : null,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>
        {/* String */}
        <div className="div-flex-column rg-30">
          {Array.isArray(getDimensionForQualityChecklog) &&
            getDimensionForQualityChecklog
              .filter((dim) => dim.data_type?.id === 3)
              .map((dimension) => (
                <div
                  key={dimension.quality_check_dimension_assignment_id}
                  className="div-flex-column rg-5 qc-icon-padding border-radius-4"
                >
                  <div className="div-flex-row div-space-between">
                    <span className="text-md div-flex-row div-align-center cg-5">
                      <span className="text-md text-semi-bold">
                        {dimension.dimension_name}
                      </span>
                      <span
                        onMouseEnter={() =>
                          setHoverQualityCheckDimensionId(
                            dimension.quality_check_dimension_assignment_id,
                          )
                        }
                        onMouseLeave={() =>
                          setHoverQualityCheckDimensionId(null)
                        }
                        className="mb-1 cursor-pointer"
                      >
                        <TargetInfoIcon className="icon-size-sm" />
                      </span>
                      {hoverQualityCheckDimensionId ===
                        dimension.quality_check_dimension_assignment_id &&
                        focusedQualityCheckDimensionId !==
                          dimension.quality_check_dimension_assignment_id &&
                        getTheFieldExtraData(dimension)}
                      {focusedQualityCheckDimensionId ===
                        dimension.quality_check_dimension_assignment_id &&
                        getTheFieldExtraData(dimension)}
                    </span>
                  </div>
                  <div className="div-flex-column rg-20">
                    <Row>
                      <Col xxl={12} xl={12} lg={12}>
                        <textarea
                          className="form-input-description text-sm"
                          placeholder={`Enter ${dimension.dimension_name}`}
                          rows={2}
                          value={
                            formData.dimensions?.find(
                              (d) =>
                                d.quality_check_dimension_assignment_id ===
                                dimension.quality_check_dimension_assignment_id,
                            )?.actual_value || ''
                          }
                          onFocus={() =>
                            setFocusedQualityCheckDimensionId(
                              dimension.quality_check_dimension_assignment_id,
                            )
                          }
                          onBlur={() => setFocusedQualityCheckDimensionId(null)}
                          onChange={(e) =>
                            handleDimensionChange(
                              dimension.quality_check_dimension_assignment_id,
                              e.target.value,
                            )
                          }
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              ))}
        </div>
        {/* Choice */}
        <div className="div-flex-column rg-30">
          {Array.isArray(getDimensionForQualityChecklog) &&
            getDimensionForQualityChecklog
              .filter((dim) => dim.data_type?.id === 4)
              .map((dimension) => (
                <div
                  key={dimension.quality_check_dimension_assignment_id}
                  className="div-flex-column rg-5  qc-icon-padding border-radius-4"
                >
                  <div className="div-flex-row div-space-between">
                    <span className="text-md div-flex-row div-align-center cg-5">
                      <span className="text-md text-semi-bold">
                        {dimension.dimension_name}
                      </span>
                      <span
                        onMouseEnter={() =>
                          setHoverQualityCheckDimensionId(
                            dimension.quality_check_dimension_assignment_id,
                          )
                        }
                        onMouseLeave={() =>
                          setHoverQualityCheckDimensionId(null)
                        }
                        className="mb-1 cursor-pointer"
                      >
                        <TargetInfoIcon className="icon-size-sm" />
                      </span>
                      {hoverQualityCheckDimensionId ===
                        dimension.quality_check_dimension_assignment_id &&
                        focusedQualityCheckDimensionId !==
                          dimension.quality_check_dimension_assignment_id &&
                        getTheFieldExtraData(dimension)}
                      {focusedQualityCheckDimensionId ===
                        dimension.quality_check_dimension_assignment_id &&
                        getTheFieldExtraData(dimension)}
                    </span>
                  </div>

                  <div className="div-flex-row cg-5">
                    <div className="input-wrapper">
                      {(() => {
                        const selectedOptionId = formData.dimensions.find(
                          (d) =>
                            d.quality_check_dimension_assignment_id ===
                            dimension.quality_check_dimension_assignment_id,
                        )?.actual_option;

                        return (
                          <FilterMultiSelect
                            isMulti={false}
                            name={`selectedValues-${dimension.dimension_id}`}
                            className="multi-select-input-section"
                            value={
                              dimension.available_values
                                ?.filter((val) => val.id === selectedOptionId)
                                ?.map((val) => ({
                                  value: val.id,
                                  label: val.name,
                                })) || []
                            }
                            onChange={(selected) =>
                              handleDropdownChange(
                                dimension.quality_check_dimension_assignment_id,
                                selected,
                              )
                            }
                            onFocus={() =>
                              setFocusedQualityCheckDimensionId(
                                dimension.quality_check_dimension_assignment_id,
                              )
                            }
                            onBlur={() =>
                              setFocusedQualityCheckDimensionId(null)
                            }
                            classNamePrefix="react-select"
                            placeholder={`Select ${dimension.dimension_name}`}
                            options={dimension.available_values?.map((val) => ({
                              value: val.id,
                              label: val.name,
                            }))}
                          />
                        );
                      })()}
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
        </div>
      </div>
      <div className="div-space-between border-top footer-content-for-page">
        <button
          className={`common-btn-outlined theme-border-color-${themeColor}`}
          onClick={() => setDrawerStep('step1')}
        >
          Back
        </button>
        <div className="div-flex-row">
          <button
            className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
            onClick={handleCreateQualityCheck}
          >
            <span className="text-sm">Submit</span>
          </button>
        </div>
      </div>
    </div>
  );
}
